import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRightIcon } from 'lucide-react';
import { PageHero } from '../components/layout/PageHero';
import { SectionHeading } from '../components/ui/SectionHeading';
import { Reveal } from '../components/ui/Reveal';
import { CtaBand } from '../components/ui/CtaBand';
import { visionPillars } from '../data/policies';
const challenges = [{
  stat: '63%',
  label: 'of Nigerians experience multidimensional poverty',
  note: 'The measure is not income alone: it is water, power, schooling and health together.'
}, {
  stat: '3 in 10',
  label: 'young Nigerians are unemployed or underemployed',
  note: 'A generation with more education than opportunity.'
}, {
  stat: '4,500MW',
  label: 'average delivered power for 220 million people',
  note: 'Less than a tenth of what an economy this size requires.'
}];
const priorities = [{
  horizon: 'Years 1–2',
  title: 'Stabilise',
  detail: 'Security in the food corridors, metering and transmission, payroll integrity, and a transparent exchange-rate regime.'
}, {
  horizon: 'Years 2–3',
  title: 'Build',
  detail: 'Manufacturing clusters, mechanisation service points, ten thousand rebuilt schools and 8,800 revitalised clinics.'
}, {
  horizon: 'Years 3–4',
  title: 'Compound',
  detail: 'Export capacity, technical education at scale, and a delivery discipline that survives a change of government.'
}];
export function Vision() {
  return <>
      <PageHero eyebrow="The vision" title="The Nigeria we can build in one term." intro="Not a wish list. Six national outcomes, each with a policy attached, a number to hit and a date to hit it by." crumbs={[{
      label: 'Home',
      to: '/'
    }, {
      label: 'Vision'
    }]} data-id="element-1503" data-mp="3hwv52wlw1uuf" />

      <section className="bg-sand-50 py-16 lg:py-24" aria-labelledby="challenge-heading" data-id="element-1504" data-mp="jhhgs70jsuu">
        <div className="mx-auto max-w-content px-4 sm:px-6 lg:px-10" data-id="element-1505" data-mp="5hj2vjrww49h">
          <SectionHeading eyebrow="The national challenge" title="Start with an honest reading of where we are" intro="These are the conditions the next administration inherits. Any plan that does not begin here is not a plan." data-id="element-1506" data-mp="3bxglrry762ym" />
          <dl className="mt-10 grid gap-6 md:grid-cols-3" data-id="element-1507" data-mp="3gxcn1jfifgah">
            {challenges.map((item, index) => <Reveal key={item.stat} delay={index * 0.04} data-id="element-1508" data-mp="3uohl4h38c0ip" data-mpi={index}>
                <div className="h-full border-t-2 border-green-600 pt-5" data-id="element-1509" data-mp="3uqu8xavo553o">
                  <dt className="font-display text-[40px] leading-none text-ink" data-id="element-1510" data-mp="3pov4rys3u45o">{item.stat}</dt>
                  <dd data-id="element-1511" data-mp="8g87fi2ohl8n">
                    <p className="mt-3 text-[16px] font-semibold text-ink" data-id="element-1512" data-mp="8fgl6xuz0zv1">{item.label}</p>
                    <p className="mt-2 text-[14px] leading-relaxed text-slate-600" data-id="element-1513" data-mp="3j7y8yaj82db">{item.note}</p>
                  </dd>
                </div>
              </Reveal>)}
          </dl>
        </div>
      </section>

      <section className="bg-ink py-16 lg:py-24" aria-labelledby="proposition-heading" data-id="element-1514" data-mp="25c5w58j61l54">
        <div className="mx-auto max-w-content px-4 sm:px-6 lg:px-10" data-id="element-1515" data-mp="ktvejkdbk35j">
          <div className="max-w-3xl" data-id="element-1516" data-mp="ppk7h4uu2lsv">
            <p className="text-[13px] font-semibold uppercase tracking-[0.16em] text-gold-400" data-id="element-1517" data-mp="psogcfj8u5pv">
              The national proposition
            </p>
            <h2 id="proposition-heading" className="mt-4 font-display text-[30px] leading-[1.14] text-white sm:text-[40px]" data-id="element-1518" data-mp="g00ntwzaqnwy">
              Nigeria’s problem is not ambition. It is the distance between what we announce and
              what we deliver.
            </h2>
            <p className="mt-6 text-[17px] leading-relaxed text-white/70" data-id="element-1519" data-mp="3o9snjpzqv52a">
              Closing that distance is the whole of this campaign. It means publishing the target
              before the ceremony, naming the officer responsible, reporting the result on time and
              accepting the consequence when it is missed. Every outcome below is written so that a
              citizen — not a consultant — can check whether it happened.
            </p>
          </div>
        </div>
      </section>

      <section className="bg-white py-16 lg:py-24" aria-labelledby="outcomes-heading" data-id="element-1520" data-mp="3iksgtcerhrt5">
        <div className="mx-auto max-w-content px-4 sm:px-6 lg:px-10" data-id="element-1521" data-mp="3ehck6g2ozrgg">
          <SectionHeading eyebrow="National outcomes" title="Six things that must be true by 2031" data-id="element-1522" data-mp="39k45ipgr65jy" />
          <ul className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3" data-id="element-1523" data-mp="34krgpz6bmoeq">
            {visionPillars.map((pillar, index) => <li key={pillar.title} data-id="element-1524" data-mp="2zs83j3bjpvag" data-mpi={index}>
                <Reveal delay={Math.min(index, 5) * 0.04} data-id="element-1525" data-mp="2uuodz79edw37">
                  <Link to="/agenda" className="group flex h-full flex-col overflow-hidden rounded-card border border-sand-200 bg-sand-50 transition-[border-color,box-shadow] duration-200 ease-emphasis hover:border-green-200 hover:shadow-card" data-id="element-1526" data-mp="2uv7woq6vgoia">
                    <span className="overflow-hidden" data-id="element-1527" data-mp="2gvf9v5ygusfl">
                      <img src={pillar.image} alt="" loading="lazy" className="aspect-[16/10] w-full object-cover transition-transform duration-300 ease-emphasis group-hover:scale-[1.02]" data-id="element-1528" data-mp="wde8idnv438i" />
                    </span>
                    <span className="flex flex-1 flex-col p-6" data-id="element-1529" data-mp="1abch12fi55g3">
                      <span className="font-display text-[21px] leading-snug text-ink" data-id="element-1530" data-mp="dmws98k7v041">
                        {pillar.title}
                      </span>
                      <span className="mt-2 text-[15px] leading-relaxed text-slate-600" data-id="element-1531" data-mp="ijdhk635jkip">
                        {pillar.description}
                      </span>
                      <span className="mt-auto inline-flex items-center gap-2 pt-5 text-[14px] font-semibold text-green-700" data-id="element-1532" data-mp="retfozaxwlu3">
                        See the policies
                        <ArrowUpRightIcon aria-hidden className="h-4 w-4 transition-transform duration-200 ease-emphasis group-hover:translate-x-0.5 group-hover:-translate-y-0.5" data-id="element-1533" data-mp="3v2mubg5n4ubx" />
                      </span>
                    </span>
                  </Link>
                </Reveal>
              </li>)}
          </ul>
        </div>
      </section>

      <section className="bg-sand-100 py-16 lg:py-24" aria-labelledby="sequence-heading" data-id="element-1534" data-mp="170fpferkwpan">
        <div className="mx-auto max-w-content px-4 sm:px-6 lg:px-10" data-id="element-1535" data-mp="jalej2qtlat1">
          <SectionHeading eyebrow="Implementation philosophy" title="Sequence matters more than scale" intro="Doing everything at once is how Nigerian plans die. This is the order of operations." data-id="element-1536" data-mp="eapaj3i4hjdh" />
          <ol className="mt-10 grid gap-6 lg:grid-cols-3" data-id="element-1537" data-mp="o7tucwohx2r4">
            {priorities.map((phase, index) => <li key={phase.title} data-id="element-1538" data-mp="23nkcdeczbwy8" data-mpi={index}>
                <Reveal delay={index * 0.05} data-id="element-1539" data-mp="1znsa1je6in1b">
                  <div className="h-full rounded-card border border-sand-200 bg-white p-7" data-id="element-1540" data-mp="1zn0gee456zdo">
                    <p className="text-[12px] font-bold uppercase tracking-[0.14em] text-green-700" data-id="element-1541" data-mp="276i320coo5eg">
                      {phase.horizon}
                    </p>
                    <h3 className="mt-2 font-display text-[24px] text-ink" data-id="element-1542" data-mp="1x9dfpx73q3w2">{phase.title}</h3>
                    <p className="mt-3 text-[15px] leading-relaxed text-slate-600" data-id="element-1543" data-mp="22883zenu8knk">
                      {phase.detail}
                    </p>
                  </div>
                </Reveal>
              </li>)}
          </ol>
        </div>
      </section>

      <CtaBand title="See how the vision becomes policy." description="Nine costed policy areas, each with first-100-day actions and published measures of progress." primary={{
      label: 'Explore the agenda',
      to: '/agenda'
    }} secondary={{
      label: 'Read the manifesto',
      to: '/manifesto'
    }} data-id="element-1544" data-mp="2uw3zig1o86ir" />
    </>;
}